import { Banner, BlockStack, Button, Checkbox, Icon, InlineStack, Select, Text, TextField } from "@shopify/polaris";
import type { SelectProps } from "@shopify/polaris";
import { DeleteIcon, LockIcon, XIcon } from "@shopify/polaris-icons";
import { useEffect, useRef, useState } from "react";
import type { FitmentFieldDataType, FitmentFieldDefinition, FitmentFieldSortDirection, FitmentFieldSortMode } from "../../app/models/FitmentCatalog";
import { getFieldSampleValues } from "../../app/utils/fitmentSampleValues";

type Props = {
    field: FitmentFieldDefinition;
    role: string;
    roleOptions: SelectProps["options"];
    requiredLocked: boolean;
    canRemove: boolean;
    onChange: (changes: Partial<FitmentFieldDefinition>) => void;
    onRoleChange: (role: string) => void;
    onClose: () => void;
    onRemove: () => void;
};

const FitmentFieldEditor = ({ field, role, roleOptions, requiredLocked, canRemove, onChange, onRoleChange, onClose, onRemove }: Props) => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const [confirmRemove, setConfirmRemove] = useState(false);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const ownerDocument = dialog.ownerDocument;
        const opener = ownerDocument.activeElement as HTMLElement | null;
        // The native top layer stays in the owning App Bridge modal document.
        dialog.showModal();
        dialog.querySelector<HTMLSelectElement>("#fitment-field-type")?.focus();
        return () => {
            dialog.close();
            // React removes the dialog before passive cleanup, so restore focus explicitly.
            if (opener?.isConnected) opener.focus();
            else ownerDocument.getElementById("fitment-add-field")?.focus();
        };
    }, []);

    useEffect(() => {
        if (!confirmRemove) return;
        const body = dialogRef.current?.querySelector<HTMLDivElement>(".pf-selector-editor__body");
        if (body) body.scrollTop = 0;
    }, [confirmRemove]);

    return (
        <dialog ref={dialogRef} className="pf-selector-editor" aria-label="Field settings" aria-modal="true" id="fitment-field-editor"
            onCancel={event => { event.preventDefault(); event.stopPropagation(); onClose(); }}>
            <div className="pf-selector-editor__heading">
                <div><Text as="h2" variant="headingMd">{field.label.trim() || "New field"}</Text><Text as="p" variant="bodySm" tone="subdued">Field settings</Text></div>
                <Button variant="tertiary" icon={XIcon} accessibilityLabel="Close field settings" onClick={onClose} />
            </div>
            <div className="pf-selector-editor__body">
            {confirmRemove && <div className="pf-selector-editor__section">
                <Banner tone="warning" title={`Remove ${field.label.trim() || "this field"}?`}>
                    <BlockStack gap="300">
                        <Text as="p">This field will be removed from the selector when you save changes.</Text>
                        <InlineStack gap="200"><Button tone="critical" onClick={onRemove}>Confirm removal</Button><Button onClick={() => setConfirmRemove(false)}>Keep field</Button></InlineStack>
                    </BlockStack>
                </Banner>
            </div>}
            <div className="pf-selector-editor__section">
                <BlockStack gap="300">
                    <Select id="fitment-field-type" label="Field type" options={roleOptions} value={role} onChange={onRoleChange} helpText="Used for imports, matching, and compatibility." />
                    <TextField id="fitment-field-label" label="Display label" value={field.label} autoComplete="off" onChange={label => onChange({ label })} helpText="Shown to customers and used across imports and admin screens." />
                    {role === "custom" && <Select label="Value type" value={field.dataType} options={[
                        { label: "Text", value: "Text" }, { label: "Whole number", value: "Number" },
                        { label: "Decimal", value: "Decimal" }, { label: "Date", value: "Date" },
                    ]} onChange={value => onChange({ dataType: value as FitmentFieldDataType })} />}
                </BlockStack>
            </div>
            <div className="pf-selector-editor__section">
                <BlockStack gap="300">
                    <Text as="h3" variant="headingSm">When is a value required?</Text>
                    {requiredLocked ? <div className="pf-selector-editor__note"><Icon source={LockIcon} /><Text as="p" variant="bodySm" tone="subdued">Year, Make, and Model are always required in fitment data and before search.</Text></div>
                        : <>
                            <Checkbox label="In fitment data" helpText="Each fitment record needs a value." checked={field.isRequired} onChange={isRequired => onChange({ isRequired })} />
                            <Checkbox label="Before customers can search" helpText="Customers must select this field." checked={field.isFilterable} disabled={!field.isRequired} onChange={isFilterable => onChange({ isFilterable })} />
                            {!field.isRequired && <Text as="p" variant="bodySm" tone="subdued">Require a value in fitment data to make this field required before search.</Text>}
                        </>}
                </BlockStack>
            </div>
            <div className="pf-selector-editor__section">
                <BlockStack gap="300">
                    <Text as="h3" variant="headingSm">Value sorting</Text>
                    <div className="pf-selector-editor__sort">
                        <Select label="Sort mode" value={field.sortMode ?? "NaturalNumeric"} options={[{ label: "Natural numeric", value: "NaturalNumeric" }, { label: "Alphabetical", value: "Alphabetical" }]} onChange={value => onChange({ sortMode: value as FitmentFieldSortMode })} />
                        <Select label="Order" value={field.sortDirection ?? (role === "year" ? "Desc" : "Asc")} options={[{ label: "Ascending", value: "Asc" }, { label: "Descending", value: "Desc" }]} onChange={value => onChange({ sortDirection: value as FitmentFieldSortDirection })} />
                    </div>
                    <div className="pf-selector-editor__example" aria-label="Example sort order">
                        <Text as="p" variant="bodySm" tone="subdued">Example order</Text>
                        <ol>{getFieldSampleValues(field).map(value => <li key={value}>{value}</li>)}</ol>
                    </div>
                </BlockStack>
            </div>
            </div>
            <div className="pf-selector-editor__foot">
                <Text as="p" variant="bodySm" tone="subdued">Edits stay in your draft. Use Save changes on the page to save them.</Text>
                <InlineStack align="space-between" gap="300">
                    <Button tone="critical" variant="tertiary" icon={DeleteIcon} disabled={!canRemove || confirmRemove} onClick={() => setConfirmRemove(true)}>Remove field</Button>
                    <Button variant="primary" onClick={onClose}>Done</Button>
                </InlineStack>
            </div>
        </dialog>
    );
};

export default FitmentFieldEditor;
